/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { useGuardedModalState } from "@src/appV2/lib/utils/useGuardedModalState";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import {
  BottomSheetWithIntent,
  type IconType,
  Text,
  TextArea,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import type { IntentVariant } from "@src/appV2/redesign/system/ui-theme";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { TIMEKEEPING_REASON_CONSTANTS } from "./constants";

type BottomSheetIntentType = "info" | "error" | "success";

interface BottomSheetIntentProps {
  intent: IntentVariant;
  intentIcon: IconType;
}

export function getBottomSheetIntent(type: BottomSheetIntentType): BottomSheetIntentProps {
  switch (type) {
    case "info":
      return { intent: "neutral", intentIcon: "info" };
    case "error":
      return { intent: "neutral", intentIcon: "triangle-exclamation" };
    case "success":
      return { intent: "neutral", intentIcon: "checkmark" };
  }
}

type Step = "picker" | "acknowledgement" | "reason";

export interface TimekeepingAdjustmentBottomSheetProps {
  modalState: UseModalState;
  // Intent banner (computed by caller)
  intent?: IntentVariant;
  intentContent?: ReactNode;
  intentIcon?: IconType;
  // Picker step
  pickerTitle: string;
  hasChanges: boolean;
  isPickerScrolling: boolean;
  isPickerValid: boolean;
  requiresApproval: boolean;
  /**
   * When false, the picker button is not disabled when there are no changes.
   * Use this for flows where a pre-filled time is valid without modification (e.g. "I already started working").
   * Defaults to true.
   */
  requiresChange?: boolean;
  /**
   * Called when the user taps Next/Save on the picker step.
   * Return false to block step advancement (e.g. show a validation error).
   * Omit to always allow advancement when the button is enabled.
   */
  onPickerConfirm?: () => boolean | Promise<boolean>;
  children: ReactNode;
  // Optional acknowledgement step between picker and reason (e.g. break policy acknowledgement)
  acknowledgementStepContent?: ReactNode;
  isAcknowledgementStepComplete?: boolean;
  // Reason step
  reasonTitle: string;
  existingReason?: string;
  /** Helper text shown beneath the reason title, e.g. when the user is editing an existing reason. */
  reasonHelperText?: string;
  /** Label for the CTA button on the reason step. Defaults to "Update timesheet". */
  reasonCtaLabel?: string;
  /** Placeholder text for the reason textarea. Defaults to "Let the workplace know why you adjusted your time." */
  reasonPlaceholder?: string;
  // Callbacks
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel?: () => void;
}

export function TimekeepingAdjustmentBottomSheet(props: TimekeepingAdjustmentBottomSheetProps) {
  const {
    modalState,
    intent,
    intentContent,
    intentIcon,
    pickerTitle,
    hasChanges,
    isPickerScrolling,
    isPickerValid,
    requiresApproval,
    requiresChange = true,
    onPickerConfirm,
    children,
    acknowledgementStepContent,
    isAcknowledgementStepComplete = false,
    reasonTitle,
    existingReason,
    reasonHelperText,
    reasonCtaLabel = "Update timesheet",
    reasonPlaceholder = "Let the workplace know why you adjusted your time.",
    onConfirm,
    onCancel,
  } = props;

  const [step, setStep] = useState<Step>("picker");
  const [reason, setReason] = useState(existingReason ?? "");
  const [isConfirming, setIsConfirming] = useState(false);
  const isEditing = isDefined(existingReason) && existingReason.length > 0;

  // Block swipe-down / backdrop-tap dismissal while an async `onConfirm` chain
  // is in flight. The Cancel button and back chevron are already gated via
  // `disabled`; this covers the BottomSheet gesture paths that bypass those.
  // The component still calls the raw `modalState.closeModal` from
  // `handleReasonConfirm`'s success path, where bypassing the guard is
  // intentional.
  const guardedModalState = useGuardedModalState(modalState, !isConfirming);

  // Stabilize intent and requiresApproval while the picker is scrolling to prevent flickering.
  // Values only update once the user lifts their finger and onChange has settled.
  const [stableIntent, setStableIntent] = useState(intent);
  const [stableIntentContent, setStableIntentContent] = useState(intentContent);
  const [stableIntentIcon, setStableIntentIcon] = useState(intentIcon);
  const [stableRequiresApproval, setStableRequiresApproval] = useState(requiresApproval);

  useEffect(() => {
    if (!isPickerScrolling) {
      setStableIntent(intent);
      setStableIntentContent(intentContent);
      setStableIntentIcon(intentIcon);
      setStableRequiresApproval(requiresApproval);
    }
  }, [isPickerScrolling, intent, intentContent, intentIcon, requiresApproval]);

  // Track scrolling in a ref so handlePickerNext can guard against mid-scroll taps
  // without putting isPickerScrolling in the button's disabled prop (which causes flicker).
  const isPickerScrollingRef = useRef(isPickerScrolling);
  isPickerScrollingRef.current = isPickerScrolling;

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setStep("picker");
      setReason(existingReason ?? "");
    }
  }, [modalState.modalIsOpen, existingReason]);

  const handlePickerNext = async () => {
    if (isPickerScrollingRef.current) {
      return;
    }

    const canProceed = (await onPickerConfirm?.()) ?? true;
    if (!canProceed) {
      return;
    }

    if (!stableRequiresApproval && requiresChange) {
      setIsConfirming(true);
      try {
        await onConfirm("");
        modalState.closeModal();
      } catch {
        // The `onConfirm` handler is responsible for surfacing user-facing
        // errors (e.g. via a toast). Swallow here so the rejection does not
        // bubble up as an unhandled promise rejection from the `void
        // handlePickerNext()` click handler. The modal intentionally stays
        // open so the worker can retry.
      } finally {
        setIsConfirming(false);
      }
    } else if (isDefined(acknowledgementStepContent)) {
      setStep("acknowledgement");
    } else {
      setStep("reason");
    }
  };

  const handleReasonConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(reason);
      modalState.closeModal();
    } catch {
      // The `onConfirm` handler is responsible for surfacing user-facing errors
      // (e.g. via a toast). Swallow here so the rejection does not bubble up as
      // an unhandled promise rejection from the `void handleReasonConfirm()`
      // click handler. The modal intentionally stays open so the worker can
      // retry without re-typing their reason.
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    if (isConfirming) {
      // Guard against the cancel/back buttons being tapped while the async
      // `onConfirm` chain is still in flight — closing the modal here would
      // leave the EWTR + clock-in mutations running in the background and the
      // worker would silently end up clocked in after believing they cancelled.
      return;
    }
    onCancel?.();
    modalState.closeModal();
  };

  const cancelButton = (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Button
        variant="text"
        size="small"
        disabled={isConfirming}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          minHeight: "unset",
          paddingY: 0,
        }}
        onClick={handleCancel}
      >
        Cancel
      </Button>
    </Box>
  );

  function renderFooter() {
    if (step === "picker") {
      return (
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isPickerValid || (requiresChange && !hasChanges) || isConfirming}
            onClick={() => {
              void handlePickerNext();
            }}
          >
            {stableRequiresApproval || !requiresChange ? "Next" : "Save"}
          </Button>
          {cancelButton}
        </DialogFooter>
      );
    }

    if (step === "acknowledgement") {
      return (
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isAcknowledgementStepComplete}
            onClick={() => setStep("reason")}
          >
            Continue
          </Button>
          {cancelButton}
        </DialogFooter>
      );
    }

    return (
      <DialogFooter orientation="vertical">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS || isConfirming}
          onClick={() => {
            void handleReasonConfirm();
          }}
        >
          {reasonCtaLabel}
        </Button>
        {cancelButton}
      </DialogFooter>
    );
  }

  return (
    <BottomSheetWithIntent
      modalState={guardedModalState}
      intent={stableIntent}
      intentContent={stableIntentContent}
      intentIcon={stableIntentIcon}
      footer={renderFooter()}
    >
      <DialogContent>
        {step === "picker" && (
          <Stack spacing={6}>
            <Title semibold variant="h4" component="h2">
              {pickerTitle}
            </Title>
            {children}
          </Stack>
        )}

        {step === "acknowledgement" && (
          <Stack spacing={6}>
            <IconButton iconType="chevron-left" variant="ghost" onClick={() => setStep("picker")} />
            {acknowledgementStepContent}
          </Stack>
        )}

        {step === "reason" && (
          <Stack spacing={6}>
            <IconButton
              iconType="chevron-left"
              variant="ghost"
              disabled={isConfirming}
              onClick={() =>
                setStep(isDefined(acknowledgementStepContent) ? "acknowledgement" : "picker")
              }
            />
            <Title semibold variant="h4" component="h2">
              {reasonTitle}
            </Title>
            {isEditing && isDefined(reasonHelperText) && (
              <Text variant="body2">{reasonHelperText}</Text>
            )}
            <Stack spacing={1}>
              <TextArea
                fullWidth
                required
                value={reason}
                error={
                  reason.length > 0 && reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS
                }
                minRows={4}
                sx={(theme) => ({
                  height: "auto",
                  minHeight: theme.spacing(12),
                  alignItems: "flex-start",
                  "& textarea": { resize: "none" },
                })}
                inputProps={{ maxLength: TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS }}
                placeholder={reasonPlaceholder}
                onChange={(event) =>
                  setReason(
                    event.target.value.slice(0, TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS)
                  )
                }
              />
              {reason.length > 0 && (
                <Text
                  variant="caption"
                  sx={{
                    color: (theme) =>
                      reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  }}
                >
                  {reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS
                    ? `Minimum ${TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS} characters required`
                    : `${reason.length}/${TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS} characters`}
                </Text>
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
/* eslint-enable max-lines */
